import { DateTime } from "luxon"
import { DayOfWeek } from "@/types"

/**
 * @summary Gets the start date of the week for the given date and start day of the week preference.
 * @param date The date to get the start of the week for.
 * @param startDayOfWeek The start day of the week preference (defaults to Monday).
 * @returns The start date of the week of the given date, based on the start day of the week preference.
 */
export function getStartDateOfWeek (date: DateTime, startDayOfWeek: DayOfWeek = DayOfWeek.Monday): DateTime {
	const dayOfWeek: DayOfWeek = date.weekday
	const daysToSubtract = (dayOfWeek - startDayOfWeek + 7) % 7
	return date.minus({ days: daysToSubtract }).startOf("day")
}

/**
 * @summary Gets the end date of the week for the given date and start day of the week preference.
 * @param date The date to get the end of the week for.
 * @param startDayOfWeek The start day of the week preference (defaults to Monday).
 * @param startOfDay Whether to return the start of the day (00:00:00.000). If false, returns the end of the day (23:59:59.999).
 * @returns The end date of the week of the given date, based on the start day of the week preference.
 */
export function getEndDateOfWeek (date: DateTime, startDayOfWeek: DayOfWeek = DayOfWeek.Monday, startOfDay: boolean = false): DateTime {
	const startOfWeekDate = getStartDateOfWeek(date, startDayOfWeek)
	const endOfWeek = startOfWeekDate.plus({ days: 6 })
	return startOfDay ? endOfWeek.startOf("day") : endOfWeek.endOf("day")
}

/**
 * @summary Gets the week number (ISO 8601) for the given date and start day of the week preference.
 * The ISO 8601 standard for week numbering has week 1 start on the first week of the year that has at least 4 days in the new year,
 * with weeks starting on the start day of the week (Monday as standard in ISO 8601).
 * @param date The date to get the week number for.
 * @param startDayOfWeek The start day of the week preference (defaults to Monday).
 * @returns The week number of the given date, based on the start day of the week preference.
 */
export function getWeekNumber (date: DateTime, startDayOfWeek: DayOfWeek = DayOfWeek.Monday): number {
	const firstWeekOfYearDeciderDay: DayOfWeek = (startDayOfWeek + 3) % 7
	const newYearsDayWeekDay: DayOfWeek = DateTime.fromObject({ year: date.year, month: 1, day: 1 }).weekday
	const daysDeciderDayOccursAfter = (firstWeekOfYearDeciderDay - newYearsDayWeekDay + 7) % 7
	const firstDeciderDayInstanceOfYear = DateTime.fromObject({ year: date.year, month: 1, day: 1 }).plus({ days: daysDeciderDayOccursAfter })
	const firstWeekOfYearStartDate = getStartDateOfWeek(firstDeciderDayInstanceOfYear, startDayOfWeek)
	const daysSinceFirstWeek = date.diff(firstWeekOfYearStartDate, "days").days

	if (daysSinceFirstWeek < 0) {
		// is the last week of the previous year
		return getWeeksInYear(date.year - 1, startDayOfWeek)
	}

	// number of weeks since start of first week of year
	const weekInYear = Math.floor(daysSinceFirstWeek / 7) + 1

	if (weekInYear > getWeeksInYear(date.year, startDayOfWeek)) {
		// is the first week of the following year
		return 1
	}

	return weekInYear
}

/**
 * @summary Gets the week number (US) for the given date and start day of the week preference.
 * The US standard for week numbering has week 1 start on 1st Jan, with subsequent weeks starting
 * on the start day of the week (Sunday as standard in the US). I.e. week 1 is of variable length (between 1 and 7 days).
 * @param date The date to get the week number for.
 * @param startDayOfWeek The start day of the week preference (defaults to Sunday as is standard in the US).
 * @returns The week number of the given date, based on the start day of the week preference.
 */
export function getWeekNumberUS (date: DateTime, startDayOfWeek: DayOfWeek = DayOfWeek.Sunday): number {
	const newYearsDay = DateTime.fromObject({ year: date.year, month: 1, day: 1 })
	const daysToAddForFirstStartDayInstance = (startDayOfWeek - newYearsDay.weekday + 7) % 7
	const firstWeekModifier = daysToAddForFirstStartDayInstance === 0 ? 0 : 1
	const firstStartDayInstance = newYearsDay.plus({ days: daysToAddForFirstStartDayInstance })
	const daysSinceFirstStartDayInstance = date.diff(firstStartDayInstance, "days").days
	const weekInYear = Math.floor(daysSinceFirstStartDayInstance / 7) + 1 + firstWeekModifier

	return weekInYear
}

/**
 * @summary Gets the number of weeks in the given year (ISO 8601), accounting for a start day of the week preference.
 * @param year The year to get the number of weeks for.
 * @param startDayOfWeek The start day of the week preference (defaults to Monday).
 * @returns The number of weeks in the given year, based on the start day of the week preference.
 */
export function getWeeksInYear (year: number, startDayOfWeek: DayOfWeek = DayOfWeek.Monday): number {
	const firstWeekOfYearDeciderDay: DayOfWeek = (startDayOfWeek + 3) % 7
	const newYearsDayWeekDay: DayOfWeek = DateTime.fromObject({ year, month: 1, day: 1 }).weekday
	const isLeapYear = DateTime.fromObject({ year }).isInLeapYear

	// year has 53 weeks if Jan 1st falls on the first week of the year decider day,
	// or the year is a leap year and Jan 1st falls on the day before the first week of the year decider day
	if (firstWeekOfYearDeciderDay === newYearsDayWeekDay ||
		(isLeapYear && getPreviousDayOfWeek(firstWeekOfYearDeciderDay) === newYearsDayWeekDay)) {
		return 53
	}

	return 52
}

/* --- Helper methods --- */

function getPreviousDayOfWeek (dayOfWeek: DayOfWeek): DayOfWeek {
	return (dayOfWeek - 1 + 7) % 7
}
